// Helpers de formato — fechas relativas, SLA y números.

export function relativeTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
}

export function formatSla(seconds: number): string {
  if (seconds <= 0) return "vencido";
  const min = Math.floor(seconds / 60);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const restMin = min % 60;
  return restMin === 0 ? `${h}h` : `${h}h ${restMin}m`;
}

// ─── SLA legible (v3) ────────────────────────────────────────────────────
// Feedback del piloto: los valores crudos ("-33m", "1h40m") no se entienden
// sin conocer la convención. Reemplazamos por texto explícito con tono
// semántico para que el operador entienda de un vistazo el estado.

export type SlaTone = "overdue" | "soon" | "ok";

export interface SlaLabel {
  /** Texto explícito, ej: "Vencido hace 33 min" / "Vence en 12 min". */
  text: string;
  /** Versión compacta para chips angostos, ej: "-33 min" evitado → "hace 33 min". */
  short: string;
  tone: SlaTone;
}

/** Umbral (segundos) bajo el cual una conversación está "por vencer" (ámbar). */
const SLA_SOON_THRESHOLD_SEC = 15 * 60;

/**
 * Humaniza una duración en segundos a español compacto:
 *   45 → "45 min", 100 min → "1h 40min", 120 min → "2h".
 */
function humanizeDuration(totalSeconds: number): string {
  const min = Math.max(0, Math.round(totalSeconds / 60));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const restMin = min % 60;
  return restMin === 0 ? `${h}h` : `${h}h ${restMin}min`;
}

/**
 * Convierte segundos restantes de SLA en una etiqueta explícita + tono.
 * - Vencido (sec < 0)      → "Vencido hace 33 min"   · tone overdue (rojo)
 * - Por vencer (< 15 min)  → "Vence en 12 min"       · tone soon    (ámbar)
 * - A tiempo (≥ 15 min)    → "Vence en 1h 40min"     · tone ok      (neutro)
 */
export function slaLabel(remainingSec: number): SlaLabel {
  if (remainingSec < 0) {
    const dur = humanizeDuration(-remainingSec);
    return { text: `Vencido hace ${dur}`, short: `hace ${dur}`, tone: "overdue" };
  }
  const dur = humanizeDuration(remainingSec);
  const tone: SlaTone = remainingSec < SLA_SOON_THRESHOLD_SEC ? "soon" : "ok";
  return { text: `Vence en ${dur}`, short: `en ${dur}`, tone };
}

/**
 * Antigüedad de llegada de un mensaje/comentario para mostrar JUNTO al SLA.
 * - Dentro de las primeras 24h → hora exacta ("10:14").
 * - Pasadas 24h                → relativo por día ("hace 1 día", "hace 3 días").
 * Complementa al SLA: ubica rápido la antigüedad sin calcularla del SLA.
 */
export function formatArrivalAge(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  if (diffMs < dayMs) {
    return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  }
  const days = Math.floor(diffMs / dayMs);
  return days === 1 ? "hace 1 día" : `hace ${days} días`;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat("es-AR").format(n);
}

export function formatPercent(value: number, fractionDigits = 1): string {
  return `${(value * 100).toFixed(fractionDigits)}%`;
}

/**
 * Formatea fecha + hora para mensajes y eventos en el inbox.
 * - Si es del día de hoy → solo hora: "14:32"
 * - Si es de ayer → "ayer 14:32"
 * - Si es de este año → "26 may 14:32"
 * - Si es de otro año → "26 may 2025 14:32"
 *
 * Pensado para que el operador siempre sepa el día sin que el panel se
 * sienta sobrecargado de strings repetidos para mensajes del día.
 */
export function formatMessageDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay) return time;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return `ayer ${time}`;

  const sameYear = d.getFullYear() === now.getFullYear();
  const dayMonth = d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "short",
  });
  if (sameYear) return `${dayMonth} ${time}`;
  return `${dayMonth} ${d.getFullYear()} ${time}`;
}

/**
 * Variante corta para listas densas (cards del inbox). No incluye la hora si
 * es de hoy — el SLA ya cubre la urgencia del día.
 */
export function formatListDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString("es-AR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return "ayer";

  const sameYear = d.getFullYear() === now.getFullYear();
  if (sameYear) {
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short" });
  }
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}
